import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const SystemConfigurationTab = () => {
  const [activeSection, setActiveSection] = useState('general');

  const configSections = [
    { id: 'general', label: 'Allgemeine Einstellungen', icon: 'Settings' },
    { id: 'security', label: 'Sicherheit', icon: 'Shield' },
    { id: 'integration', label: 'Integrationen', icon: 'Link' },
    { id: 'performance', label: 'Leistung', icon: 'Zap' },
    { id: 'backup', label: 'Sicherung & Wiederherstellung', icon: 'Database' }
  ];

  const integrationStatus = [
    {
      name: 'LDAP/Active Directory',
      status: 'Verbunden',
      lastSync: '26.11.2025 18:30',
      health: 'Gut',
      endpoint: 'ldap://ad.arbeitsagentur.local:389'
    },
    {
      name: 'Sozialversicherungsdatenbank',
      status: 'Verbunden',
      lastSync: '26.11.2025 19:00',
      health: 'Gut',
      endpoint: 'https://api.sozialversicherung.de/v2'
    },
    {
      name: 'Berichtssystem',
      status: 'Verbunden',
      lastSync: '26.11.2025 18:45',
      health: 'Gut',
      endpoint: 'https://reports.arbeitsagentur.de/api'
    },
    {
      name: 'E-Mail Gateway',
      status: 'Fehler',
      lastSync: '26.11.2025 17:15',
      health: 'Kritisch',
      endpoint: 'smtp://mail.arbeitsagentur.de:587'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Section Navigation */}
      <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-2">
        <div className="flex flex-wrap gap-2">
          {configSections?.map((section) => (
            <button
              key={section?.id}
              onClick={() => setActiveSection(section?.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeSection === section?.id
                  ? 'bg-blue-600 text-white' :'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
              }`}
            >
              <Icon name={section?.icon} size={16} />
              {section?.label}
            </button>
          ))}
        </div>
      </div>
      {/* General Settings */}
      {activeSection === 'general' && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
              Anwendungseinstellungen
            </h3>
            <div className="space-y-4">
              <Input
                label="Anwendungsname"
                type="text"
                value="Bundesagentur für Arbeit Portal"
                description="Wird in der Kopfzeile und im Browser-Tab angezeigt"
              />
              <Input
                label="Support-E-Mail"
                type="email"
                value="support@arbeitsagentur.de"
                description="Kontakt-E-Mail für Benutzeranfragen"
              />
              <Select
                label="Standard-Sprache"
                options={[
                  { value: 'de', label: 'Deutsch' },
                  { value: 'en', label: 'Englisch' }
                ]}
                value="de"
              />
              <Select
                label="Zeitzone"
                options={[
                  { value: 'Europe/Berlin', label: 'Europa/Berlin (MEZ)' },
                  { value: 'Europe/London', label: 'Europa/London (GMT)' }
                ]}
                value="Europe/Berlin"
              />
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
              Sitzungsverwaltung
            </h3>
            <div className="space-y-4">
              <Input
                label="Sitzungs-Timeout (Minuten)"
                type="number"
                value="30"
                description="Automatische Abmeldung nach Inaktivität"
              />
              <Input
                label="Maximale gleichzeitige Sitzungen"
                type="number"
                value="3"
                description="Anzahl der erlaubten aktiven Sitzungen pro Benutzer"
              />
              <Checkbox
                label="Warnung vor Sitzungsablauf anzeigen"
                checked
                description="Benutzer 5 Minuten vor Ablauf benachrichtigen"
              />
            </div>
          </div>
        </div>
      )}
      {/* Security Settings */}
      {activeSection === 'security' && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
              Authentifizierung
            </h3>
            <div className="space-y-4">
              <Checkbox
                label="Multi-Faktor-Authentifizierung (MFA) erzwingen"
                checked
                description="Alle Benutzer müssen MFA aktivieren"
              />
              <Checkbox
                label="Passwort-Komplexität erzwingen"
                checked
                description="Mindestens 12 Zeichen, Groß-/Kleinbuchstaben, Zahlen und Sonderzeichen"
              />
              <Input
                label="Passwort-Ablauf (Tage)"
                type="number"
                value="90"
                description="Benutzer müssen Passwort regelmäßig ändern"
              />
              <Input
                label="Maximale Anmeldeversuche"
                type="number"
                value="5"
                description="Konto wird nach fehlgeschlagenen Versuchen gesperrt"
              />
              <Input
                label="Kontosperrzeit (Minuten)"
                type="number"
                value="30"
                description="Dauer der automatischen Kontosperre"
              />
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
              Datenschutz & Verschlüsselung
            </h3>
            <div className="space-y-4">
              <Checkbox
                label="Daten im Ruhezustand verschlüsseln"
                checked
                description="AES-256 Verschlüsselung für gespeicherte Daten"
              />
              <Checkbox
                label="TLS 1.3 für alle Verbindungen erzwingen"
                checked
                description="Sichere Übertragung aller Daten"
              />
              <Checkbox
                label="IP-Whitelist aktivieren"
               
                description="Nur Zugriff von genehmigten IP-Adressen"
              />
            </div>
          </div>
        </div>
      )}
      {/* Integration Settings */}
      {activeSection === 'integration' && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
            <div className="p-6 border-b border-slate-200 dark:border-slate-700">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  Systemintegrationen
                </h3>
                <Button variant="outline" iconName="RefreshCw" iconPosition="left">
                  Alle testen
                </Button>
              </div>
            </div>
            <div className="divide-y divide-slate-200 dark:divide-slate-700">
              {integrationStatus?.map((integration, index) => (
                <div key={index} className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                          {integration?.name}
                        </h4>
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                          integration?.status === 'Verbunden' ?'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300' :'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                        }`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${
                            integration?.status === 'Verbunden' ? 'bg-emerald-600' : 'bg-red-600'
                          }`} />
                          {integration?.status}
                        </span>
                      </div>
                      <div className="text-sm text-slate-600 dark:text-slate-400 space-y-1">
                        <div>Endpunkt: <span className="font-mono text-xs">{integration?.endpoint}</span></div>
                        <div>Letzte Synchronisation: {integration?.lastSync}</div>
                        <div>Systemzustand: <span className={
                          integration?.health === 'Gut' ? 'text-emerald-600' : 'text-red-600'
                        }>{integration?.health}</span></div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" iconName="Settings">
                        Konfigurieren
                      </Button>
                      <Button variant="outline" size="sm" iconName="Activity">
                        Testen
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {/* Performance Settings */}
      {activeSection === 'performance' && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
              Leistungsoptimierung
            </h3>
            <div className="space-y-4">
              <Checkbox
                label="Datenbankabfrage-Caching aktivieren"
                checked
                description="Verbessert die Antwortzeiten für häufige Abfragen"
              />
              <Input
                label="Cache-Ablaufzeit (Minuten)"
                type="number"
                value="15"
                description="Wie lange zwischengespeicherte Daten gültig bleiben"
              />
              <Checkbox
                label="Lazy Loading für große Datensätze"
                checked
                description="Lädt Daten schrittweise für bessere Performance"
              />
              <Input
                label="Maximale Datensätze pro Seite"
                type="number"
                value="50"
                description="Anzahl der Einträge in Tabellen und Listen"
              />
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
              Systemressourcen
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-900 rounded-lg">
                <div>
                  <div className="text-sm font-medium text-slate-900 dark:text-slate-100">CPU-Auslastung</div>
                  <div className="text-xs text-slate-600 dark:text-slate-400 mt-1">Durchschnitt der letzten Stunde</div>
                </div>
                <div className="text-2xl font-bold text-emerald-600">23%</div>
              </div>
              <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-900 rounded-lg">
                <div>
                  <div className="text-sm font-medium text-slate-900 dark:text-slate-100">Speichernutzung</div>
                  <div className="text-xs text-slate-600 dark:text-slate-400 mt-1">Von 32 GB verfügbar</div>
                </div>
                <div className="text-2xl font-bold text-blue-600">18.4 GB</div>
              </div>
              <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-900 rounded-lg">
                <div>
                  <div className="text-sm font-medium text-slate-900 dark:text-slate-100">Datenbankgröße</div>
                  <div className="text-xs text-slate-600 dark:text-slate-400 mt-1">Gesamtspeicher verwendet</div>
                </div>
                <div className="text-2xl font-bold text-purple-600">245 GB</div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Backup Settings */}
      {activeSection === 'backup' && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
              Automatische Sicherung
            </h3>
            <div className="space-y-4">
              <Checkbox
                label="Automatische tägliche Sicherung aktivieren"
                checked
                description="Vollständige Systemsicherung jeden Tag um 02:00 Uhr"
              />
              <Input
                label="Aufbewahrungsdauer (Tage)"
                type="number"
                value="30"
                description="Ältere Sicherungen werden automatisch gelöscht"
              />
              <Select
                label="Sicherungsspeicherort"
                options={[
                  { value: 'local', label: 'Lokaler Server' },
                  { value: 'cloud', label: 'Cloud-Speicher' },
                  { value: 'both', label: 'Beide' }
                ]}
                value="both"
              />
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
              Letzte Sicherungen
            </h3>
            <div className="space-y-3">
              {[
                { date: '26.11.2025 02:00', size: '12.4 GB', status: 'Erfolgreich' },
                { date: '25.11.2025 02:00', size: '12.3 GB', status: 'Erfolgreich' },
                { date: '24.11.2025 02:00', size: '12.2 GB', status: 'Erfolgreich' }
              ]?.map((backup, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-900 rounded-lg">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                      <Icon name="Database" size={20} className="text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-900 dark:text-slate-100">{backup?.date}</div>
                      <div className="text-xs text-slate-600 dark:text-slate-400">Größe: {backup?.size}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-emerald-600 dark:text-emerald-400 font-medium">
                      {backup?.status}
                    </span>
                    <Button variant="outline" size="sm" iconName="Download">
                      Wiederherstellen
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3">
            <Button variant="default" iconName="Database" iconPosition="left">
              Manuelle Sicherung starten
            </Button>
            <Button variant="outline" iconName="Upload" iconPosition="left">
              Sicherung hochladen
            </Button>
          </div>
        </div>
      )}
      {/* Save Actions */}
      <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700">
        <div className="text-sm text-slate-600 dark:text-slate-400">
          Änderungen werden sofort wirksam. Kritische Änderungen erfordern eine Bestätigung.
        </div>
        <div className="flex gap-3">
          <Button variant="outline">
            Zurücksetzen
          </Button>
          <Button variant="default" iconName="Save" iconPosition="left">
            Änderungen speichern
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SystemConfigurationTab;